import { Component, OnInit, OnDestroy } from '@angular/core';
import { AuthSignalService, User } from '../../core/state/auth.signal';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { InboxService } from './inbox/services/inbox.service';
import { UnreadService } from '../../core/services/unread.service';
import { AuthService } from '../../core/auth/auth.service';
import { Subscription, combineLatest } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CONFIG_HUB_VIEWS, SettingsViewMeta } from '../settings/settings-views.config';

@Component({
  selector: 'app-dashboard',
  standalone: false,
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, OnDestroy {
  isSidebarOpen = false;
  user: User | null = null;
  isAdmin = false;
  isSuperAdmin = false;
  phoneAccounts: any[] = [];
  selectedAccountId: number | null = null;
  private chatCountMap: Map<number, number> = new Map();
  private subs = new Subscription();
  isZohoModalOpen = false;

  /** Permisos de las vistas dentro de "Configuración" (el hub /settings). */
  readonly configSectionPerms = [
    'users.view', 'roles.view', 'teams.view', 'availability.config_view', 'support.view',
    'cobradores.view', 'recordatorios.view', 'rec_campanas.view', 'faqs.view', 'anuncios.view',
    'followups.view', 'campaigns.view', 'phone_accounts.view',
  ];

  /** El bloque "Administración" del sidebar se muestra si hay Configuración o Bitácora. */
  get adminSectionPerms(): string[] {
    return [...this.configSectionPerms, 'audit.view'];
  }

  /** Rutas hijas del colapsable "Configuración" del sidebar. */
  readonly configViews: SettingsViewMeta[] = CONFIG_HUB_VIEWS;
  /** Estado del submenú "Configuración" (abierto/cerrado). */
  configOpen = false;

  constructor(
    private authSignal: AuthSignalService,
    public router: Router,
    private http: HttpClient,
    private inboxService: InboxService,
    private unreadService: UnreadService,
    private authService: AuthService
  ) { }

  ngOnInit() {
    this.user = this.authSignal.user();
    this.isAdmin = this.authSignal.isAdmin();
    this.isSuperAdmin = this.authSignal.isSuperAdmin();

    // Load phone accounts: quien puede ver todas las líneas (phone_accounts.view) las ve todas;
    // el resto solo las de sus equipos.
    const canSeeAllLines = this.authSignal.has('phone_accounts.view');
    const accountsEndpoint = canSeeAllLines
      ? `${environment.apiUrl}/phone-accounts`
      : `${environment.apiUrl}/phone-accounts/my`;

    this.http.get<any[]>(accountsEndpoint).subscribe({
      next: (accounts) => { this.phoneAccounts = accounts; },
      error: () => {}
    });

    // El submenú de Configuración arranca abierto si ya estamos en una vista de settings.
    this.configOpen = this.router.url.startsWith('/settings');

    // Conversaciones para los contadores del panel: quien tiene la pestaña "Todos"
    // (chats.tab_all) usa todas; el resto solo su vista global (asignados+equipo+sin asignar).
    const convSource$ = this.authSignal.has('chats.tab_all')
      ? this.inboxService.allConvs$
      : this.inboxService.globalConvs$;

    this.subs.add(
      combineLatest([
        convSource$,
        this.unreadService.state$
      ]).subscribe(([convs, unreadMap]) => {
        const map = new Map<number, number>();
        for (const conv of convs) {
          const accId = (conv as any).phoneAccount?.id ?? null;
          if (accId !== null && unreadMap.has(conv.id)) {
            map.set(accId, (map.get(accId) ?? 0) + 1);
          }
        }
        this.chatCountMap = map;
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /** Líneas visibles en el panel lateral: solo las activas (las desactivadas se ocultan;
   *  siguen gestionables en Settings → Números WhatsApp para reactivarlas). */
  get visibleAccounts(): any[] {
    return this.phoneAccounts.filter((a) => a?.active !== false);
  }

  chatCountForAccount(accountId: number): number {
    return this.chatCountMap.get(accountId) ?? 0;
  }

  selectAccount(accountId: number | null) {
    this.selectedAccountId = accountId;
    this.inboxService.selectedAccountId = accountId;
    this.inboxService.loadAllTabs();
    this.router.navigate(['/inbox']);
  }

  isInboxRoute(): boolean {
    return this.router.url.includes('/inbox');
  }

  isSettingsRoute(): boolean {
    return this.router.url.startsWith('/settings');
  }

  /** Abre/cierra el submenú de Configuración. Al abrir desde fuera de settings, va al hub. */
  toggleConfig() {
    this.configOpen = !this.configOpen;
    if (this.configOpen && !this.isSettingsRoute()) {
      this.router.navigate(['/settings']);
    }
  }

  unlinkZoho() {
    this.isZohoModalOpen = true;
  }

  confirmUnlinkZoho() {
    this.http.post(`${environment.apiUrl}/zoho/unlink`, {}).subscribe({
      next: () => {
        this.isZohoModalOpen = false;
        this.authSignal.setZohoLinked(false);
        this.router.navigate(['/link-zoho']);
      },
      error: (err) => {
        alert('Hubo un error al intentar desvincular la cuenta.');
        console.error(err);
      }
    });
  }

  logout() {
    this.authService.logout();
  }
}
